/**
 * 判定型检查项的详情：逐 case 结果（design D15 第 3 层「判定型」）。
 *
 * 默认只列不通过的 case（后端已把不通过的排前面），可切换显示全部。
 * 「通过（未上报）」= 场景名单里有该检查项但没有结果行，按「名单 − 失败」口径视为通过。
 * 日志按钮以 taskId 为键（日志对象 key 是 ``<taskId>/...`` 前缀），没有 taskId 时禁用。
 */
import { useMemo, useState } from 'react';
import { Button, Switch, Table, Tag, Tooltip } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import type { PerceptionCheckerCaseRow } from '@/apis/simulation';
import ApiCodeLink from '@components/ApiCodeLink';
import { sceneGroupDisplay } from '@/constants/sceneGroup';

import { EMPTY } from '../format';

const UNLABELED = '__unlabeled__';

interface CheckerCaseTableProps {
  rows: PerceptionCheckerCaseRow[];
  loading?: boolean;
  /** 打开 Task 日志 Modal（来自 useTaskLogModal） */
  onViewLog: (taskId: number) => void;
}

const resultTag = (r: PerceptionCheckerCaseRow) => {
  if (!r.passed) {
    return (
      <Tag color="red" bordered={false}>
        不通过
      </Tag>
    );
  }
  if (!r.reported) {
    return (
      <Tooltip title="名单里有该检查项但没有结果行，按「名单 − 失败」视为通过">
        <Tag bordered={false}>通过（未上报）</Tag>
      </Tooltip>
    );
  }
  return (
    <Tag color="green" bordered={false}>
      通过
    </Tag>
  );
};

export default function CheckerCaseTable({ rows, loading, onViewLog }: CheckerCaseTableProps) {
  const [showAll, setShowAll] = useState(false);
  const failCount = useMemo(() => rows.filter((r) => !r.passed).length, [rows]);
  const data = useMemo(
    () => (showAll ? rows : rows.filter((r) => !r.passed)),
    [rows, showAll],
  );

  const columns: ColumnsType<PerceptionCheckerCaseRow> = [
    {
      title: 'Case',
      dataIndex: 'caseId',
      key: 'caseId',
      width: 160,
      render: (v: string | null) => v || EMPTY,
    },
    {
      title: 'API_CODE',
      dataIndex: 'apiCode',
      key: 'apiCode',
      width: 130,
      render: (v: string | null, r) => <ApiCodeLink code={v} source={r.source} />,
    },
    {
      title: '场景分组',
      dataIndex: 'sceneGroup',
      key: 'sceneGroup',
      width: 120,
      render: (v: string | null) => sceneGroupDisplay(v),
    },
    {
      title: '场景标签',
      dataIndex: 'label',
      key: 'label',
      width: 140,
      ellipsis: true,
      render: (v: string | null) => (!v || v === UNLABELED ? EMPTY : v),
    },
    { title: '结果', key: 'passed', width: 120, render: (_, r) => resultTag(r) },
    {
      title: '说明',
      dataIndex: 'msg',
      key: 'msg',
      ellipsis: { showTitle: false },
      render: (v: string | null) =>
        v ? (
          <Tooltip title={v} placement="topLeft">
            {v}
          </Tooltip>
        ) : (
          EMPTY
        ),
    },
    {
      title: '操作',
      key: 'action',
      width: 70,
      fixed: 'right',
      render: (_, r) => (
        <Tooltip title={r.taskId ? 'Task 日志' : '该场景无 task，无日志'}>
          <Button
            type="text"
            size="small"
            icon={<FileTextOutlined />}
            disabled={!r.taskId}
            onClick={() => r.taskId && onViewLog(r.taskId)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#6b6f76' }}>
        <Switch size="small" checked={showAll} onChange={setShowAll} />
        <span>
          显示全部（共 {rows.length} 个运行 case，不通过 {failCount} 个）
        </span>
      </div>
      <Table<PerceptionCheckerCaseRow>
        rowKey={(r) => `${r.sceneId ?? 'none'}-${r.taskId ?? 'none'}`}
        size="small"
        loading={loading}
        dataSource={data}
        columns={columns}
        tableLayout="fixed"
        scroll={{ x: 1000 }}
        locale={{
          emptyText: showAll ? '暂无运行 case' : '没有不通过的 case，可打开「显示全部」查看',
        }}
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100', '500'],
          showTotal: (t) => `总共 ${t} 行`,
        }}
      />
    </div>
  );
}
